import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PhotoIcon,
  TrashIcon,
} from "@heroicons/react/24/outline"
import { Button, Input, Select } from "~/ui-rtk/components/ui/controls"
import { Toggle } from "~/ui-rtk/components/ui/controls/Toggle"
import { useAdForgeListOverlayLayoutsQuery } from "~/ui-rtk/api/adForgeApi"
import {
  useCreateMegaFeedStructureMutation,
  useUpdateMegaFeedStructureMutation,
  type MegaFeedMediaType,
  type MegaFeedSlotKind,
  type MegaFeedSlotKindOption,
  type MegaFeedStructure,
  type SaveMegaFeedStructureSlot,
} from "~/ui-rtk/api/megaFeedStructureApi"
import LibraryAssetPicker, { type PickedAsset } from "./LibraryAssetPicker"

/** Meta's supplemental-feed caps; the API enforces the same. */
export const MAX_IMAGE_SLOTS = 10
export const MAX_VIDEO_SLOTS = 3

/** A slot being edited; `key` keeps React rows stable across reorders. */
interface DraftSlot extends SaveMegaFeedStructureSlot {
  key: string
  exampleAssetUrl?: string
  fixedAssetUrl?: string
}

const newKey = () => Math.random().toString(36).slice(2, 11)

const toDraft = (structure?: MegaFeedStructure): DraftSlot[] =>
  structure?.slots.map(slot => ({
    key: slot.id,
    mediaType: slot.mediaType,
    kind: slot.kind,
    criterion: slot.criterion ?? undefined,
    label: slot.label ?? undefined,
    description: slot.description ?? undefined,
    exampleAssetId: slot.exampleAssetId ?? undefined,
    exampleAssetUrl: slot.exampleAssetUrl ?? undefined,
    fixedAssetId: slot.fixedAssetId ?? undefined,
    fixedAssetUrl: slot.fixedAssetUrl ?? undefined,
    allowGeneration: slot.allowGeneration,
  })) ?? []

const MEDIA_TYPE_OPTIONS = [
  { label: "Image", value: "IMAGE" },
  { label: "Video", value: "VIDEO" },
]

type Props = {
  vocabulary: MegaFeedSlotKindOption[]
  /** Edit this structure; a new one when absent. */
  structure?: MegaFeedStructure
  onSaved: (structure: MegaFeedStructure) => void
  onCancel: () => void
}

function AssetPick({
  label,
  url,
  isVideo = false,
  required = false,
  onPick,
  onClear,
}: {
  label: string
  url?: string
  isVideo?: boolean
  required?: boolean
  onPick: () => void
  onClear: () => void
}) {
  return (
    <div className="flex items-center gap-system-xs">
      <button
        type="button"
        onClick={onPick}
        title={
          url
            ? `Replace ${label.toLowerCase()}`
            : `Choose ${label.toLowerCase()} from the library or upload`
        }
        className="w-12 h-12 rounded-md border border-dashed border-basic-blue flex items-center justify-center overflow-hidden bg-basic-dark-blue disabled:opacity-50"
      >
        {url && isVideo && (
          <video src={url} className="w-full h-full object-cover" muted />
        )}
        {url && !isVideo && (
          <img src={url} alt={label} className="w-full h-full object-cover" />
        )}
        {!url && <PhotoIcon className="w-5 h-5 text-cadet-blue" />}
      </button>
      <div className="flex flex-col text-xs">
        <span className="text-basic-white">{label}</span>
        {url ? (
          <button
            type="button"
            onClick={onClear}
            className="text-cadet-blue hover:text-basic-white text-left"
          >
            Remove
          </button>
        ) : (
          <span className="text-cadet-blue">
            {required ? "Required" : "Optional"}
          </span>
        )}
      </div>
    </div>
  )
}

type SlotRowProps = {
  index: number
  count: number
  slot: DraftSlot
  option?: MegaFeedSlotKindOption
  kindOptions: { label: string; value: string }[]
  layoutOptions: { label: string; value: string }[]
  mediaTypeDisabled: boolean
  onPatch: (change: Partial<DraftSlot>) => void
  onKind: (kind: MegaFeedSlotKind) => void
  onMove: (delta: number) => void
  onRemove: () => void
  /** Opens the library picker for the slot's example image or fixed asset. */
  onPickAsset: (target: "example" | "fixed") => void
}

function SlotRow({
  index,
  count,
  slot,
  option,
  kindOptions,
  layoutOptions,
  mediaTypeDisabled,
  onPatch,
  onKind,
  onMove,
  onRemove,
  onPickAsset,
}: SlotRowProps) {
  const isFixed = slot.kind === "FIXED_ASSET"
  const criterionOptions =
    slot.kind === "BRAND_TEMPLATE" ? layoutOptions : (option?.criteria ?? [])

  return (
    <div className="flex gap-system-sm items-start bg-system-blue-800 rounded-lg p-3">
      <div className="flex flex-col items-center gap-1 pt-1 w-6 shrink-0">
        <span className="text-xs font-semibold text-basic-white">
          {index + 1}
        </span>
        <button
          type="button"
          aria-label="Move up"
          disabled={index === 0}
          onClick={() => onMove(-1)}
          className="text-cadet-blue hover:text-basic-white disabled:opacity-30"
        >
          <ArrowUpIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          aria-label="Move down"
          disabled={index === count - 1}
          onClick={() => onMove(1)}
          className="text-cadet-blue hover:text-basic-white disabled:opacity-30"
        >
          <ArrowDownIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-system-xs">
        <div className="grid grid-cols-1 md:grid-cols-[110px_1fr_1fr] gap-system-xs">
          <Select
            aria-label="Media type"
            options={MEDIA_TYPE_OPTIONS}
            value={slot.mediaType}
            disabled={mediaTypeDisabled}
            onChange={e =>
              onPatch({
                mediaType: e.target.value as MegaFeedMediaType,
                fixedAssetId: undefined,
                fixedAssetUrl: undefined,
              })
            }
          />
          <Select
            aria-label="Slot kind"
            options={kindOptions}
            value={slot.kind}
            onChange={e => onKind(e.target.value as MegaFeedSlotKind)}
          />
          {criterionOptions.length > 0 ? (
            <Select
              aria-label="Criterion"
              options={criterionOptions}
              placeholder={slot.kind === "BRAND_TEMPLATE" ? "Layout" : "Pick"}
              value={slot.criterion ?? ""}
              onChange={e => onPatch({ criterion: e.target.value })}
            />
          ) : (
            <span className="text-xs text-cadet-blue self-center">
              {slot.kind === "BRAND_TEMPLATE"
                ? "No overlay layouts yet: create one in AdForge."
                : option?.description}
            </span>
          )}
        </div>
        {option && criterionOptions.length > 0 && (
          <span className="text-xs text-cadet-blue">{option.description}</span>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-system-xs">
          <Input
            aria-label="Label"
            placeholder="Label (optional)"
            value={slot.label ?? ""}
            onChange={e => onPatch({ label: e.target.value })}
            fullWidth
          />
          <Input
            aria-label="Shot notes"
            placeholder="Shot notes, e.g. close-up of the collar stitching"
            value={slot.description ?? ""}
            onChange={e => onPatch({ description: e.target.value })}
            fullWidth
          />
        </div>

        <div className="flex items-center gap-system-sm flex-wrap">
          {slot.mediaType === "IMAGE" && !isFixed && (
            <AssetPick
              label="Example image"
              url={slot.exampleAssetUrl}
              onPick={() => onPickAsset("example")}
              onClear={() =>
                onPatch({
                  exampleAssetId: undefined,
                  exampleAssetUrl: undefined,
                })
              }
            />
          )}
          {isFixed && (
            <AssetPick
              label={slot.mediaType === "IMAGE" ? "Fixed image" : "Fixed video"}
              url={slot.fixedAssetUrl}
              isVideo={slot.mediaType === "VIDEO"}
              required
              onPick={() => onPickAsset("fixed")}
              onClear={() =>
                onPatch({ fixedAssetId: undefined, fixedAssetUrl: undefined })
              }
            />
          )}
          {!isFixed && (
            <label className="flex items-center gap-system-xs text-xs text-basic-white">
              <Toggle
                size="xs"
                value={slot.allowGeneration}
                onChange={value => onPatch({ allowGeneration: value })}
              />
              Generate when missing
              <span className="text-cadet-blue">
                · AI images can look off; prefer picking existing ones
              </span>
            </label>
          )}
        </div>
      </div>

      <button
        type="button"
        aria-label="Remove slot"
        onClick={onRemove}
        className="text-cadet-blue hover:text-red-400 pt-1"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

/**
 * Structure editor: the ordered slots of a structured feed. Each slot is a
 * kind (top performer, shot type, …) plus its criterion, with optional notes
 * and an example image the generator follows.
 */
export default function StructureEditor({
  vocabulary,
  structure,
  onSaved,
  onCancel,
}: Props) {
  const [name, setName] = useState(structure?.name ?? "")
  const [description, setDescription] = useState(structure?.description ?? "")
  const [slots, setSlots] = useState<DraftSlot[]>(() => toDraft(structure))

  const [createStructure, { isLoading: isCreating }] =
    useCreateMegaFeedStructureMutation()
  const [updateStructure, { isLoading: isUpdating }] =
    useUpdateMegaFeedStructureMutation()
  const { data: layouts = [] } = useAdForgeListOverlayLayoutsQuery()
  // The slot whose example image / fixed asset the library picker is choosing.
  const [picking, setPicking] = useState<{
    key: string
    target: "example" | "fixed"
    mediaType: MegaFeedMediaType
  } | null>(null)

  const optionByKind = useMemo(
    () => new Map(vocabulary.map(option => [option.kind, option])),
    [vocabulary],
  )
  const kindOptions = vocabulary.map(option => ({
    label: option.label,
    value: option.kind,
  }))

  const imageCount = slots.filter(s => s.mediaType === "IMAGE").length
  const videoCount = slots.length - imageCount
  const isFull = (mediaType: MegaFeedMediaType) =>
    mediaType === "IMAGE"
      ? imageCount >= MAX_IMAGE_SLOTS
      : videoCount >= MAX_VIDEO_SLOTS

  const patch = (key: string, change: Partial<DraftSlot>) =>
    setSlots(current =>
      current.map(slot => (slot.key === key ? { ...slot, ...change } : slot)),
    )

  const addSlot = (mediaType: MegaFeedMediaType) => {
    const first = vocabulary.find(o => o.kind === "SHOT_TYPE") ?? vocabulary[0]
    setSlots(current => [
      ...current,
      {
        key: newKey(),
        mediaType,
        kind: first?.kind ?? "SHOT_TYPE",
        criterion: first?.criteria[0]?.value,
        // Off by default: generated images often look off, reuse comes first.
        allowGeneration: false,
      },
    ])
  }

  const move = (index: number, delta: number) =>
    setSlots(current => {
      const next = [...current]
      const [slot] = next.splice(index, 1)
      next.splice(index + delta, 0, slot)
      return next
    })

  const changeKind = (slot: DraftSlot, kind: MegaFeedSlotKind) => {
    const option = optionByKind.get(kind)
    patch(slot.key, {
      kind,
      criterion:
        kind === "BRAND_TEMPLATE" ? layouts[0]?.id : option?.criteria[0]?.value,
      fixedAssetId: undefined,
      fixedAssetUrl: undefined,
    })
  }

  const choose = (asset: PickedAsset) => {
    if (!picking) return
    patch(
      picking.key,
      picking.target === "example"
        ? { exampleAssetId: asset.id, exampleAssetUrl: asset.url }
        : { fixedAssetId: asset.id, fixedAssetUrl: asset.url },
    )
  }

  const problem = useMemo(() => {
    if (!name.trim()) return "Name the structure."
    if (slots.length === 0) return "Add at least one slot."
    for (const [i, slot] of slots.entries()) {
      const option = optionByKind.get(slot.kind)
      if (slot.kind === "FIXED_ASSET" && !slot.fixedAssetId) {
        return `Slot ${i + 1}: upload the fixed asset.`
      }
      if (option?.criterionRequired && !slot.criterion) {
        return `Slot ${i + 1}: pick what fills it.`
      }
    }
    return null
  }, [name, slots, optionByKind])

  const save = async () => {
    if (problem) return
    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      slots: slots.map(slot => ({
        mediaType: slot.mediaType,
        kind: slot.kind,
        criterion: slot.criterion || undefined,
        label: slot.label?.trim() || undefined,
        description: slot.description?.trim() || undefined,
        exampleAssetId:
          slot.mediaType === "IMAGE" ? slot.exampleAssetId : undefined,
        fixedAssetId:
          slot.kind === "FIXED_ASSET" ? slot.fixedAssetId : undefined,
        allowGeneration: slot.allowGeneration,
      })),
    }
    try {
      const saved = structure
        ? await updateStructure({ id: structure.id, ...body }).unwrap()
        : await createStructure(body).unwrap()
      toast.success("Structure saved.")
      onSaved(saved)
    } catch (err) {
      const message = (err as { data?: { message?: string | string[] } })?.data
        ?.message
      toast.error(
        Array.isArray(message)
          ? message.join(", ")
          : (message ?? "Couldn't save the structure."),
      )
    }
  }

  return (
    <div className="flex flex-col gap-system-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-system-sm">
        <Input
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Apparel PDP"
          fullWidth
        />
        <Input
          label="Description"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional"
          fullWidth
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-cadet-blue">
          {imageCount}/{MAX_IMAGE_SLOTS} images · {videoCount}/{MAX_VIDEO_SLOTS}{" "}
          videos. The first slot is the feed&apos;s main image.
        </span>
        <div className="flex gap-system-xs">
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            onClick={() => addSlot("IMAGE")}
            disabled={isFull("IMAGE")}
          >
            + Image slot
          </Button>
          <Button
            variant={{ variant: "outlined" }}
            size="sm"
            onClick={() => addSlot("VIDEO")}
            disabled={isFull("VIDEO")}
          >
            + Video slot
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-system-xs max-h-[55vh] overflow-y-auto pr-1">
        {slots.length === 0 && (
          <div className="text-sm text-cadet-blue p-4 text-center border border-dashed border-basic-blue rounded-lg">
            No slots yet. Add the shots every product should carry, in order.
          </div>
        )}
        {slots.map((slot, index) => (
          <SlotRow
            key={slot.key}
            index={index}
            count={slots.length}
            slot={slot}
            option={optionByKind.get(slot.kind)}
            kindOptions={kindOptions}
            layoutOptions={layouts.map(l => ({ label: l.name, value: l.id }))}
            mediaTypeDisabled={isFull(
              slot.mediaType === "IMAGE" ? "VIDEO" : "IMAGE",
            )}
            onPatch={change => patch(slot.key, change)}
            onKind={kind => changeKind(slot, kind)}
            onMove={delta => move(index, delta)}
            onRemove={() =>
              setSlots(current => current.filter(s => s.key !== slot.key))
            }
            onPickAsset={target =>
              setPicking({ key: slot.key, target, mediaType: slot.mediaType })
            }
          />
        ))}
      </div>

      <div className="flex items-center justify-end gap-system-sm">
        {problem && <span className="text-xs text-cadet-blue">{problem}</span>}
        <Button variant={{ variant: "outlined" }} size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => void save()}
          disabled={Boolean(problem)}
          isLoading={isCreating || isUpdating}
        >
          {isCreating || isUpdating ? "Saving…" : "Save structure"}
        </Button>
      </div>

      <LibraryAssetPicker
        isOpen={picking !== null}
        mediaType={
          picking?.target === "example"
            ? "IMAGE"
            : (picking?.mediaType ?? "IMAGE")
        }
        title={
          picking?.target === "example"
            ? "Pick an example image"
            : `Pick the fixed ${picking?.mediaType === "VIDEO" ? "video" : "image"}`
        }
        onClose={() => setPicking(null)}
        onSelect={choose}
      />
    </div>
  )
}
